import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Staff() {
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "waiter", password: "" });

  const load = async () => {
    const { data } = await api.get("/staff");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", form);
      toast.success("Mitarbeiter hinzugefügt");
      setShow(false);
      setForm({ name: "", email: "", role: "waiter", password: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Mitarbeiter entfernen?")) return;
    try {
      await api.delete(`/staff/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const ROLE_COLORS = {
    owner: "text-gold",
    manager: "text-blue-300",
    host: "text-emerald-300",
    waiter: "text-[var(--text-primary)]",
    bartender: "text-purple-300",
    admin: "text-gold",
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="staff-title">{t("staff.title")}</h1>
        </div>
        <button onClick={() => setShow(true)} data-testid="staff-new-btn" className="btn-primary text-xs inline-flex items-center gap-2"><Plus className="w-3.5 h-3.5" />{t("staff.new")}</button>
      </div>

      <div className="surface-1 rounded-md overflow-hidden">
        {members.length === 0 && (
          <div className="p-12 text-center text-sm text-[var(--text-dim)]" data-testid="staff-empty">{t("staff.empty")}</div>
        )}
        {members.map((m) => (
          <div key={m.user_id} data-testid={`staff-${m.user_id}`} className="flex items-center justify-between px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center text-gold text-sm">
                {m.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div>
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-[var(--text-muted)]">{m.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-xs uppercase tracking-wider ${ROLE_COLORS[m.role] || ""}`}>{t(`staff.roles.${m.role}`) || m.role}</span>
              {m.role !== "owner" && (
                <button onClick={() => handleDelete(m.user_id)} data-testid={`staff-delete-${m.user_id}`} className="text-[var(--text-dim)] hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setShow(false)}>
          <div className="surface-1 rounded-md w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-light mb-6">{t("staff.new")}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label={t("common.name")}>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" className="input" />
              </Field>
              <Field label={t("common.email")}>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" className="input" />
              </Field>
              <Field label={t("staff.role")}>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role-select" className="input">
                  <option value="manager">{t("staff.roles.manager")}</option>
                  <option value="host">{t("staff.roles.host")}</option>
                  <option value="waiter">{t("staff.roles.waiter")}</option>
                  <option value="bartender">{t("staff.roles.bartender")}</option>
                </select>
              </Field>
              <Field label={`${t("common.password")} (${t("common.optional")})`}>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" className="input" placeholder="Lässt sich später setzen" />
              </Field>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShow(false)} className="btn-ghost text-xs flex-1">{t("common.cancel")}</button>
                <button type="submit" data-testid="staff-submit" className="btn-primary text-xs flex-1">{t("common.add")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;background:#141414;border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:0 16px;height:44px;font-size:14px;color:#F5F5F0;outline:none;}.input:focus{border-color:#D4AF37;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[var(--text-muted)] mb-2 block uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
